import React from "react";
import {
  Anchor,
  CalendarDays,
  MapPin,
  Sparkles,
} from "lucide-react";
import "./Landing.css";

const rsvpUrl =
  "https://docs.google.com/forms/d/e/1FAIpQLScSYhMfeYs2ydToILswIMpkvK-FAKBYQMTV7WaLHcKAfm_zrg/viewform?usp=sharing&ouid=108000536225047704825&pli=1&authuser=0";

type ScheduleEvent = {
  time: string;
  title: string;
  description?: string;
  note?: string;
  featured?: boolean;
};

type ScheduleDay = {
  day: string;
  events: ScheduleEvent[];
};

const schedule: ScheduleDay[] = [
  {
    day: "Friday",
    events: [
      {
        time: "5:30–9:00 PM",
        title: "Casual dinner at Willow Camp",
        description:
          "Come for dinner, stay and linger, or head out early for bedtime.",
      },
    ],
  },
  {
    day: "Saturday",
    events: [
      { time: "7:00 AM", title: "Dawn Patrol 🏄" },
      { time: "8:30–9:30 AM", title: "Breakfast in the Lodge" },
      { time: "9:30–10:30 AM", title: "Yoga on the Pavilion 🧘" },
      {
        time: "11:30 AM",
        title: "🎉 Official Party Time 🎉",
        note: "Comfortable, beach-casual attire",
        featured: true,
      },
      {
        time: "12:00–1:15 PM",
        title: "Puppet Show on the Main Lawn 🎪",
      },
      {
        time: "12:30–2:00 PM",
        title: "Raw Bar & Grazing Stations on the Pavilion 🦪",
      },
      { time: "Afternoon", title: "Beach time 🏖️" },
      {
        time: "4:30 PM",
        title: "✨ Shift into Evening Mode ✨",
        note:
          "Comfortable, casual cocktail attire. We’ll be outside on grass and gravel.",
        featured: true,
      },
      { time: "4:30 PM", title: "Treasure Hunt 🏴‍☠️" },
      {
        time: "4:30–6:00 PM",
        title: "Cocktails & Hors d’Oeuvres Around the Property 🍹",
      },
      { time: "6:00 PM", title: "Songs by Ira Wolf 🎶" },
      { time: "6:45 PM", title: "Sunset Toast 🥂" },
      {
        time: "7:00 PM",
        title: "Kids Movie & Popcorn on the Pavilion 🎥🍿",
      },
      {
        time: "7:30 PM",
        title: "“Late” Night Bites & Indoor Revelry in the Lodge 🪩",
      },
    ],
  },
  {
    day: "Sunday",
    events: [
      { time: "8:30–9:30 AM", title: "Breakfast in the Lodge" },
      {
        time: "10:00–11:30 AM",
        title: "Steep Ravine–Dipsea Trail Hike ⛰️🥾",
        note: "A little over 3 miles, mostly downhill.",
      },
      { time: "12:00 PM", title: "Lunch at Parkside Cafe" },
    ],
  },
];

const photos = [
  {
    src: "/anniversary/assets/pool-enhanced.png",
    alt: "Pool terrace overlooking the ocean",
  },
  {
    src: "/anniversary/assets/oysters.jpg",
    alt: "Oysters and lemons for cocktail hour",
  },
  {
    src: "/anniversary/assets/garden-path.jpg",
    alt: "Garden path strung with lights at Willow Camp",
  },
];

const dayPhotos = [
  {
    src: "/anniversary/assets/beach-trail.jpg",
    alt: "Trail above Stinson Beach",
  },
  {
    src: "/anniversary/assets/willow-lodge.jpg",
    alt: "Willow Camp lodge living room",
  },
];

export default function AnniversaryLanding() {
  return (
    <main className="anniversary-site">
      <section className="anniversary-hero" aria-label="Invitation">
        <img
          src="/anniversary/assets/camp-aerial.jpeg"
          alt="Willow Camp and the Stinson Beach coastline"
          className="anniversary-hero__image"
        />
        <div className="anniversary-hero__shade" />
        <div className="anniversary-hero__content">
          <h1>Come celebrate love at Stinson Beach</h1>
          <p className="anniversary-deck">
            Fifteen years of Luke and Lauren, gathered with the people we love
            at Willow Camp.
          </p>
          <div className="anniversary-hero__details" aria-label="Event details">
            <span>
              <CalendarDays size={18} aria-hidden="true" />
              Saturday, October 3, 2026
            </span>
            <span>
              <MapPin size={18} aria-hidden="true" />
              Willow Camp, Stinson Beach, CA
            </span>
          </div>
          <a className="anniversary-rsvp" href={rsvpUrl}>
            RSVP Here
          </a>
        </div>
      </section>

      <section className="anniversary-intro" id="weekend">
        <div>
          <h2>A full day celebration at Stinson Beach.</h2>
        </div>
        <p>
          We've been married fifteen years! To celebrate, we've planned a full
          day of family fun at Willow Camp, to spend with our family and
          community in one of our favorite places in California. We hope that
          you and yours will come join us.
        </p>
      </section>

      <section className="anniversary-photo-strip" aria-label="Willow Camp views">
        {photos.map((photo) => (
          <img key={photo.src} src={photo.src} alt={photo.alt} />
        ))}
      </section>

      <section className="anniversary-schedule" id="day">
        <div className="anniversary-section-heading">
          <p className="anniversary-kicker">Friday through Sunday</p>
          <h2>What the weekend holds</h2>
        </div>
        <div className="anniversary-itinerary">
          {schedule.map((day) => (
            <section className="anniversary-itinerary__day" key={day.day}>
              <h3>{day.day}</h3>
              <div className="anniversary-itinerary__events">
                {day.events.map((event) => (
                  <article
                    className={`anniversary-itinerary__event${
                      event.featured
                        ? " anniversary-itinerary__event--featured"
                        : ""
                    }`}
                    key={`${event.time}-${event.title}`}
                  >
                    <time>{event.time}</time>
                    <div>
                      <h4>{event.title}</h4>
                      {event.description && <p>{event.description}</p>}
                      {event.note && (
                        <p className="anniversary-itinerary__note">
                          {event.note}
                        </p>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
        <div className="anniversary-day-photos" aria-label="Willow Camp interiors and coast">
          {dayPhotos.map((photo) => (
            <img key={photo.src} src={photo.src} alt={photo.alt} />
          ))}
        </div>
      </section>

      <section className="anniversary-stay" id="stay">
        <img
          src="/anniversary/assets/garden-cabin.jpg"
          alt="Garden cabin at Willow Camp"
        />
        <div>
          <p>
            If you are interested in staying overnight Friday and/or Saturday,
            we can help arrange nearby lodging.
          </p>
          <a className="anniversary-rsvp anniversary-rsvp--dark" href={rsvpUrl}>
            RSVP Here
          </a>
        </div>
      </section>

      <section className="anniversary-closing">
        <Sparkles size={24} strokeWidth={1.6} aria-hidden="true" />
        <h2>We cannot wait to celebrate with you.</h2>
        <a href="https://maps.google.com/?q=Willow+Camp+Stinson+Beach+CA">
          <Anchor size={18} aria-hidden="true" />
          Open Map
        </a>
        <a className="anniversary-rsvp anniversary-rsvp--dark" href={rsvpUrl}>
          RSVP Here
        </a>
      </section>
    </main>
  );
}
